import * as React from 'react';
import { ReactComponent as Add } from '../../assets/icons/plus--small.svg';
import { RecipientButton } from '../RecipientButton/RecipientButton';
import type { Group, Group as GroupType } from '../../containers/GroupsList/groupsSlice/types';
import { useWindowSize } from '../../utils/customHooks/useWindowSize';
import { translate } from '../../utils/translate';
import { SMoreText } from '../../containers/CreateMessage/CreateMessageSummary.styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { RecipientType } from '../../containers/RecipientGroups/RecipientGroups';
import { SAddButton, SContainer } from './RecipientsSummaryList.styles';
import { getDisplayedItems } from './helpers';
import type { GroupInMessageDetail, Org } from '../../containers/ChatsList/chatListSlice/types';
import type { UserInfoViewModelApp } from '../../apis/authApi/types';
import { selectUserId } from '../../containers/Login/LoginSlice';
import { useAppSelector } from '../../hooks';
import type { ExternalContact } from '../../apis/externalContacts/types';
import type { SelectListUser } from '../../containers/CreateMessage/createMessageSlice/types';
import type { SelectedGroup } from '../../containers/CreateMessage/SelectGroupsList';

type SyntheticRecipient = { id: number; name: string; isSynthetic: true };

export type DataType =
  | Group
  | UserInfoViewModelApp
  | Org
  | GroupInMessageDetail
  | SelectListUser
  | SelectedGroup
  | ExternalContact
  | SyntheticRecipient;

type IRecipientsSummaryListProps = {
  data: DataType[];
  nameProperty?: string;
  recipientsType?: RecipientType;
  maxCharsCount?: number;
  sortByProperty?: boolean;
  marginHorizontal?: number;
  recipientGroupsRoute?: string;
  handleAddClick?: () => void;
  handleRemoveClick?: (id: number) => void;
};

export const RecipientsSummaryList = ({
  data,
  nameProperty = 'name',
  recipientsType = RecipientType.Groups,
  maxCharsCount,
  sortByProperty,
  marginHorizontal = 20,
  recipientGroupsRoute = '/message/recipient-groups',
  handleAddClick,
  handleRemoveClick,
}: IRecipientsSummaryListProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const authUserId = useAppSelector(selectUserId);
  const [width] = useWindowSize();
  const haveAddButton = !!handleAddClick;
  const haveRemoveButton = !!handleRemoveClick;
  const [displayedItems, setDisplayedItems] = React.useState<GroupType[]>([]);

  const loadDisplayedItems = async () => {
    if (data.length > 1) {
      const loadedItems = await getDisplayedItems({
        itemsList: data as unknown as DataType[],
        nameProperty: nameProperty,
        width,
        maxCharsCount,
        sortByProperty,
        authUserId: recipientsType === RecipientType.Users ? authUserId : undefined,
        marginHorizontal,
        haveAddButton,
        haveRemoveButton,
      });
      if (loadedItems.length) {
        setDisplayedItems(loadedItems as unknown as GroupType[]);
        return;
      }
    }
    setDisplayedItems(data as unknown as GroupType[]);
  };

  React.useEffect(() => {
    loadDisplayedItems();
  }, [data, width]);

  const navigateToRecipientGroups = () => {
    if (!recipientGroupsRoute) return;
    navigate(recipientGroupsRoute, {
      state: {
        prevPath: location.pathname,
        data: data
          .filter(d => !(d as SyntheticRecipient).isSynthetic)
          .map(item => ({
            id: item.id,
            name: item[nameProperty as keyof DataType] as unknown as string,
            imagePath:
              recipientsType === RecipientType.Groups
                ? (item as Group).imageFileName
                : recipientsType === RecipientType.Users
                  ? (item as UserInfoViewModelApp).photoFileName
                  : undefined,
            parentId:
              recipientsType === RecipientType.Accounts ? (item as Org).parentId : undefined,
            subtitle:
              recipientsType === RecipientType.Users && authUserId !== item.id
                ? (item as UserInfoViewModelApp).email
                : recipientsType === RecipientType.Accounts
                  ? translate(
                      (item as Org).parentId
                        ? 'messages_broadcast_subaccount'
                        : 'messages_broadcast_account'
                    )
                  : undefined,
          })),
        recipientsType,
      },
    });
  };

  const isAuthUser = (item: DataType) => {
    return recipientsType === RecipientType.Users && authUserId === item?.id;
  };

  const getName = (item: DataType) => {
    if (isAuthUser(item)) return translate('you');
    return item[nameProperty as keyof DataType] as unknown as string;
  };

  return (
    <SContainer role="horizontalScrollContainer">
      {haveAddButton && (
        <SAddButton role="addButton" onClick={handleAddClick}>
          <Add role="addIcon" />
        </SAddButton>
      )}
      {displayedItems.map((listItem, index) => (
        <RecipientButton
          key={`${listItem.id}-${index}`}
          name={getName(listItem)}
          hideRemove={!haveRemoveButton || isAuthUser(listItem)}
          onClick={haveRemoveButton ? () => handleRemoveClick?.(listItem.id) : undefined}
        />
      ))}
      {data.length > displayedItems?.length && (
        <SMoreText onClick={() => navigateToRecipientGroups()}>{`+${
          data.length - displayedItems?.length
        } ${translate('more')}`}</SMoreText>
      )}
    </SContainer>
  );
};
